<template>
  <q-dialog
    v-model="setMarketValueHistoryDialog.show"
    @keydown.escape="close()"
    persistent
    transition-show="scale"
    transition-hide="scale"
    maximized
  >
    <q-card>
      <div class="q-pa-md">
        <q-inner-loading
          :showing="loading"
          label="Please wait..."
          label-class="text-teal"
          label-style="font-size: 1.1em"
        />
        <div v-if="!loading && vehicle">
          <transition appear enter-active-class="animated fadeIn">
            <div class="text-center">
              <h6 style="margin: 0 0 15px">Market value history</h6>
              <div class="row q-col-gutter-sm">
                <div class="col col-sm-12 col-md-4">
                  <strong>Vin:</strong> {{ vehicle.vin }} <strong>Unit:</strong> {{ vehicle.unit }}
                </div>
                <div class="col col-sm-12 col-md-4">
                  <strong>Model year:</strong> {{ vehicle.model_year }}
                </div>
                <div class="col col-sm-12 col-md-4">
                  <strong>Company: </strong>{{ vehicle.company.name }}
                  <strong>Location: </strong>{{ vehicle.location.name }} ({{ vehicle.location.code }})
                </div>
              </div>
            </div>
          </transition>

          <transition appear enter-active-class="animated fadeIn">
            <div v-if="data.length > 0">
              <vue-highcharts :options="chartOptions"></vue-highcharts>

              <q-table :data="changesData" :columns="changesColumns" class="no-shadow container">
                <template v-slot:body="props">
                  <q-tr :props="props">
                    <q-td key="two_days" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.two_days)"
                           v-if="props.row.two_days !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.two_days)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.two_days }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="seven_days" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.seven_days)"
                           v-if="props.row.seven_days !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.seven_days)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.seven_days }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="one_month" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.one_month)"
                           v-if="props.row.one_month !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.one_month)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.one_month }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="three_months" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.three_months)"
                           v-if="props.row.three_months !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.three_months)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.three_months }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="six_months" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.six_months)"
                           v-if="props.row.six_months !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.six_months)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.six_months }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="one_year" :props="props">
                      <div class="flex flex-center"
                           :style="changeColor(props.row.one_year)"
                           v-if="props.row.one_year !== null">
                        <q-icon :name="`fas fa-${changeIcon(props.row.one_year)}`"
                                size="10px" style="margin-right: 3px;"></q-icon>
                        <span>{{ props.row.one_year }}%</span>
                      </div>
                      <span v-else>-</span>
                    </q-td>
                  </q-tr>
                </template>
              </q-table>

              <q-table
                :data="data"
                :columns="columns"
                row-key="name"
                :pagination="{ page: 1, rowsPerPage: 0, sortBy: 'date', descending: true }"
                hide-bottom
                class="no-shadow container"
              >
                <template v-slot:body="props">
                  <q-tr :props="props">
                    <q-td key="date" :props="props">{{ props.row.date | dateFormat }}</q-td>
                    <q-td key="mileage" :props="props">{{ props.row.mileage | numberWithCommas }}</q-td>
                    <q-td key="good" :props="props">
                      <span v-if="props.row.excellent">{{ props.row.good | toCurrency }}</span>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="one_year_older_condition_price" :props="props">
                      <span v-if="props.row.excellent">{{
                          props.row.one_year_older_condition_price | toCurrency
                        }}</span>
                      <span v-else>-</span>
                    </q-td>
                    <q-td key="one_year_older_mileage" :props="props">
                      <span v-if="props.row.excellent">{{ props.row.one_year_older_mileage | numberWithCommas }}</span>
                      <span v-else>-</span>
                    </q-td>
                  </q-tr>
                </template>
              </q-table>
            </div>
            <div v-else class="text-center">
              <p class="text-center">No data!</p>
            </div>
          </transition>
        </div>
        <q-btn
          v-bind:disabled="loading"
          style="position: absolute; top: 20px; right: 20px;"
          color="red-7"
          label="X"
          @click="close()"
        ></q-btn>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import axios from '@/axios'
import {Chart} from 'highcharts-vue'
import moment from 'moment'

export default {
  name: 'VehicleMarketValueHistoryDialog',
  components: {
    VueHighcharts: Chart
  },
  data() {
    return {
      loading: false,
      show: false,
      vehicle: null,
      columns: [
        {
          name: 'date',
          align: 'center',
          label: 'Date',
          field: 'date',
          sortable: true
        },
        {
          name: 'mileage',
          align: 'left',
          label: 'Mileage',
          field: 'mileage',
          sortable: true
        },
        {
          name: 'good',
          align: 'center',
          label: 'Price',
          field: 'good',
          sortable: true
        }
      ],
      data: [],
      changesColumns: [
        {
          name: 'two_days',
          align: 'center',
          label: '48H',
          field: 'two_days',
          sortable: false
        },
        {
          name: 'seven_days',
          align: 'center',
          label: '7D',
          field: 'seven_days',
          sortable: false
        },
        {
          name: 'one_month',
          align: 'center',
          label: '30D',
          field: 'one_month',
          sortable: false
        },
        {
          name: 'three_months',
          align: 'center',
          label: '90D',
          field: 'three_months',
          sortable: false
        },
        {
          name: 'six_months',
          align: 'center',
          label: '180D',
          field: 'six_months',
          sortable: false
        },
        {
          name: 'one_year',
          align: 'center',
          label: '1Y',
          field: 'one_year',
          sortable: false
        }
      ],
      changesData: [],
      chartOptions: {
        title: '',
        credits: false,
        chart: {
          zoomType: 'xy'
        },
        xAxis: {
          categories: [],
          labels: {
            enabled: true,
            rotation: -60,
            step: 7
          }
        },
        yAxis: [
          {
            labels: {
              format: '${value:.,0f}',
            },
            title: {
              text: 'Price ($)'
            },
            showFirstLabel: false,
          },
          {
            title: {
              text: 'Mileage'
            },
            showFirstLabel: false,
            opposite: true
          }
        ],
        plotOptions: {
          line: {
            marker: {
              enabled: false
            }
          }
        },
        colors: ['#ff773c', '#41e0b4', '#4f80ea'],
        series: [
          {
            name: 'Price',
            type: 'line',
            yAxis: 0,
            data: []
          },
          {
            name: 'Mileage',
            type: 'line',
            yAxis: 1,
            data: []
          }
        ]
      }
    }
  },
  computed: {
    setMarketValueHistoryDialog: {
      get() {
        return this.$store.state.vehicle.setMarketValueHistoryDialog
      },
      set(val) {
        this.$store.commit('vehicle/SET_MARKET_VALUE_HISTORY_DIALOG', val)
      }
    }
  },
  methods: {
    async fetchValues() {
      this.loading = true

      try {
        const {data} = await axios.get(`vehicle/${this.setMarketValueHistoryDialog.id}/history`)
        this.vehicle = {
          id: data.data.id,
          vin: data.data.vin,
          unit: data.data.unit,
          company: data.data.company,
          location: data.data.location,
          brand: data.data.location,
          model: data.data.model,
          model_year: data.data.model_year,
          condition: data.data.condition,
          changes: data.data.changes
        }

        this.data = data.data.mileage_records.map(record => {
          return {
            mileage: record.mileage,
            date: record.market_values.length > 0 ? record.market_values[0].updated_at : record.date,
            excellent: record.market_values.length > 0 ? record.market_values[0].excellent : null,
            good: record.market_values.length > 0 ? record.market_values[0].good : null,
            fair: record.market_values.length > 0 ? record.market_values[0].fair : null,
            one_year_older_condition_price:
              record.market_values.length > 0 ? record.market_values[0].one_year_older_condition_price : null,
            one_year_older_mileage:
              record.market_values.length > 0 ? record.market_values[0].one_year_older_mileage : null
          }
        })

        this.changesData = [
          {
            two_days: this.vehicle.changes.two_days[this.vehicle.condition],
            seven_days: this.vehicle.changes.seven_days[this.vehicle.condition],
            one_month: this.vehicle.changes.one_month[this.vehicle.condition],
            three_months: this.vehicle.changes.three_months[this.vehicle.condition],
            six_months: this.vehicle.changes.six_months[this.vehicle.condition],
            one_year: this.vehicle.changes.one_year[this.vehicle.condition]
          }
        ]

        this.setChartData()

        this.loading = false
      } catch (e) {
        this.$q.notify({
          color: 'negative',
          message: 'Failed to load market value history',
          icon: 'error',
          position: 'top'
        })

        this.loading = false
        this.close()
      }
    },
    setChartData() {
      const mileageData = []
      const priceData = []
      const dateData = []
      this.data.reverse().forEach((record, _) => {
        mileageData.push(record.mileage)
        priceData.push(record.good)
        dateData.push(moment(record.date).format('MM.DD.YYYY'))
      })

      this.chartOptions.series[0].data = priceData
      this.chartOptions.series[1].data = mileageData
      this.chartOptions.xAxis.categories = dateData
    },
    changeColor(val) {
      if (val > 0) return 'color:green'
      else if (val < 0) return 'color:red'
      else return 'color:gray'
    },
    changeIcon(val) {
      if (val > 0) return 'arrow-up'
      else if (val < 0) return 'arrow-down'
      else return 'minus'
    },
    close() {
      this.loading = false
      this.$store.commit('vehicle/SET_MARKET_VALUE_HISTORY_DIALOG', {
        show: false,
        id: null
      })
    }
  },
  watch: {
    setMarketValueHistoryDialog: {
      deep: true,
      async handler() {
        if (this.setMarketValueHistoryDialog.id !== null && this.setMarketValueHistoryDialog.show) {
          await this.fetchValues()
        }
      }
    }
  }
}
</script>

<style scoped>
.container {
  margin: auto;
  max-width: 720px;
}

.changes {
  margin: 25px auto;
  max-width: 720px;
  text-align: center;
}
</style>
